import { useEffect, useState } from 'react';
import {
  SimpleGrid,
  Paper,
  Text,
  Group,
  ThemeIcon,
  LoadingOverlay,
  Title,
  Modal,
  Table,
  Badge,
  Button,
  ScrollArea,
  Stack,
  Box,
  rem,
  ActionIcon,
  Tooltip,
  Center,
  Progress,
} from '@mantine/core';
import {
  IconUsers,
  IconAlertTriangle,
  IconDashboard,
  IconArrowRight,
  IconSearch,
  IconClockHour4,
  IconCalendarTime,
  IconBuildingCommunity,
  IconMapPin,
  IconUserExclamation,
  IconFileCertificate,
  IconShieldCheck,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

// Projene uygun axios importu (yolu kendi projene göre ayarlarsın)
import api from '../../api/axiosConfig'; 
import EmployeeProfileModal from '../../components/HumanResources/EmployeeProfileModal';

dayjs.locale('tr');

export default function HrDashboard() {
  const [stats, setStats] = useState({
    total_employees: 142,
    on_leave_count: 5,
    document_alerts: 12,
    total_locations: 8,
    location_counts: {
      'Merkez': 35,
      'A Şantiyesi': 60,
      'B Şantiyesi': 27,
      'C Şantiyesi': 20,
    },
  });

  const [loading, setLoading] = useState(false); // Backend bağlanana kadar false

  const [listOpened, { open: openList, close: closeList }] = useDisclosure(false);
  const [profileOpened, { open: openProfile, close: closeProfile }] = useDisclosure(false);

  const [alertList, setAlertList] = useState([]);
  const [alertType, setAlertType] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Şantiye/Lokasyon renk ve ikon konfigürasyonu
  const locationConfig = {
    'Merkez': { icon: IconBuildingCommunity, color: 'blue', label: 'Merkez Ofis' },
    'A Şantiyesi': { icon: IconMapPin, color: 'cyan', label: 'A Şantiyesi' },
    'B Şantiyesi': { icon: IconMapPin, color: 'indigo', label: 'B Şantiyesi' },
    'C Şantiyesi': { icon: IconMapPin, color: 'teal', label: 'C Şantiyesi' },
  };

  // Backend'den veri çekme şablonu (Backend yazılınca aktif edebilirsin)
  const fetchStats = async () => {
  
    try {
      setLoading(true);
      const response = await api.get('/hr/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('HR Dashboard verileri çekilemedi:', error);
    } finally {
      setLoading(false);
    }
    
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleCardClick = async (type) => {
    if (!['leave', 'document'].includes(type)) return;

    setAlertType(type);
    setLoadingList(true);
    openList();

    // Geçici Mock Data (Backend yazılınca api.get ile değiştirilecek)
    setTimeout(() => {
      if (type === 'leave') {
        setAlertList([
          { id: 1, name: 'Ahmet Yılmaz', role: 'Saha Mühendisi', info: 'Yıllık İzin', target_date: dayjs().add(3, 'day').toISOString() },
          { id: 2, name: 'Ayşe Kaya', role: 'İnsan Kaynakları Uzmanı', info: 'Sağlık Raporu', target_date: dayjs().add(1, 'day').toISOString() },
        ]);
      } else if (type === 'document') {
        setAlertList([
          { id: 3, name: 'Kemal Can', role: 'Usta', info: 'Yüksekte Çalışma Belgesi', target_date: dayjs().add(12, 'day').toISOString() },
          { id: 4, name: 'Mehmet Demir', role: 'Şoför', info: 'T.C. Kimlik Fotokopisi', target_date: dayjs().subtract(5, 'day').toISOString(), isMissing: true },
        ]);
      }
      setLoadingList(false);
    }, 600);
  };

  const handleEmployeeDetailClick = (employee) => {
    setSelectedEmployee(employee);
    openProfile();
  };

  const getDaysLeft = (dateStr, isMissing = false) => {
    if (isMissing) return { text: 'Eksik Evrak', color: 'red', variant: 'filled' };
    
    const diff = dayjs(dateStr).diff(dayjs(), 'day');
    if (diff < 0) return { text: `${Math.abs(diff)} Gün Geçti`, color: 'red', variant: 'filled' };
    if (diff <= 7) return { text: `${diff} Gün Kaldı`, color: 'orange', variant: 'light' };
    return { text: `${diff} Gün Kaldı`, color: 'yellow', variant: 'outline' };
  };

  const getModalInfo = () => {
    switch (alertType) {
      case 'leave':
        return { title: 'İzindeki Personeller', icon: IconCalendarTime, color: 'blue' };
      case 'document':
        return { title: 'Dikkat Gerektiren Evraklar', icon: IconAlertTriangle, color: 'red' };
      default:
        return { title: 'Personel Listesi', icon: IconUsers, color: 'blue' };
    }
  };

  const statData = [
    {
      id: 'total',
      title: 'Toplam Personel',
      value: stats.total_employees,
      icon: IconUsers,
      color: 'blue',
      desc: 'Şirket bünyesindeki aktif çalışanlar',
      clickable: false,
    },
    {
      id: 'leave',
      title: 'İzindeki Personel',
      value: stats.on_leave_count,
      icon: IconCalendarTime,
      color: 'orange',
      desc: 'Şu an aktif izinde olan personeller',
      clickable: true,
    },
    {
      id: 'document',
      title: 'Evrak Uyarıları',
      value: stats.document_alerts,
      icon: IconAlertTriangle,
      color: 'red',
      desc: 'Eksik İSG ve özlük belgeleri',
      clickable: true,
    },
    {
      id: 'location',
      title: 'Aktif Lokasyon',
      value: stats.total_locations,
      icon: IconBuildingCommunity,
      color: 'green',
      desc: 'Merkez ve aktif şantiye sayısı',
      clickable: false,
    },
  ];

  const modalInfo = getModalInfo();
  const upcomingEvents = [
    { id: 1, title: 'Ahmet Yılmaz izin dönüşü', meta: 'A Şantiyesi · 10 Ekim 2026', color: 'blue' },
    { id: 2, title: 'Kemal Can · Yüksekte Çalışma Belgesi', meta: '12 gün içinde yenilenmeli', color: 'orange' },
    { id: 3, title: 'Elif Kara işe giriş yıldönümü', meta: '20 Ekim 2026', color: 'grape' },
  ];
  const recentActivities = [
    { id: 1, title: 'Ayşe Kaya için sağlık raporu eklendi', meta: 'Bugün · 09:42' },
    { id: 2, title: 'Ahmet Yılmaz yıllık izin talebi oluşturdu', meta: 'Bugün · 08:15' },
    { id: 3, title: 'Mehmet Demir profili güncellendi', meta: 'Dün · 16:30' },
  ];

  return (
        <Box p={{ base: 'md', lg: 'xl' }} w="100%" h="100%">
      <LoadingOverlay visible={loading} overlayProps={{ radius: 'sm', blur: 2 }} />

      <Stack gap="xl">
        {/* HERO SECTION */}
        <Paper
          radius="xl"
          p={{ base: 'lg', md: 'xl' }}
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 42%, rgba(37,99,235,0.88) 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 50px rgba(15,23,42,0.18)',
          }}
        >
          <Box
            style={{ position: 'absolute', top: -80, right: -80, width: 240, height: 240, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }}
          />
          <Box
            style={{ position: 'absolute', bottom: -60, left: -60, width: 180, height: 180, borderRadius: '50%', background: 'rgba(96,165,250,0.18)' }}
          />

          <Group justify="space-between" align="flex-start" wrap="wrap" style={{ position: 'relative', zIndex: 1 }}>
            <Stack gap={8}>
              <Group gap="sm">
                <ThemeIcon size={50} radius="xl" variant="black" color="rgba(255,255,255,0.14)">
                  <IconDashboard size={26} color="white" />
                </ThemeIcon>
                <div>
                  <Text size="sm" fw={700} c="rgba(255,255,255,0.72)">İnsan Kaynakları Yönetimi</Text>
                  <Title order={2} fw={900} c="white" style={{ letterSpacing: '-0.8px' }}>Genel Bakış</Title>
                </div>
              </Group>
              <Text size="sm" maw={720} style={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.7 }}>
                Şirket personelinin mevcut durumunu, izin takip çizelgelerini ve şantiye bazlı eksik evrak uyarılarını tek ekrandan kontrol edebilirsiniz.
              </Text>
            </Stack>

            <Paper
              radius="xl"
              p="md"
              style={{
                minWidth: 240,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <Group wrap="nowrap" align="center">
                <ThemeIcon size={42} radius="xl" variant="black" color="rgba(255,255,255,0.14)">
                  <IconClockHour4 size={20} color="white" />
                </ThemeIcon>
                <div>
                  <Text size="xs" fw={700} c="rgba(255,255,255,0.70)">Sistem Saati</Text>
                  <Text fw={800} c="white">{dayjs().format('DD.MM.YYYY HH:mm')}</Text>
                </div>
              </Group>
            </Paper>
          </Group>
        </Paper>

        {/* KPI CARDS */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
          {statData.map((stat, index) => (
            <Paper
              key={stat.id}
              withBorder
              p="lg"
              radius="xl"
              onClick={() => stat.clickable && handleCardClick(stat.id)}
              style={{
                cursor: stat.clickable ? 'pointer' : 'default',
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(226,232,240,0.95)',
                boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                animation: `fadeUp 420ms ease ${index * 70}ms both`,
              }}
              onMouseEnter={(e) => {
                if (!stat.clickable) return;
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 18px 40px rgba(15,23,42,0.10)';
              }}
              onMouseLeave={(e) => {
                if (!stat.clickable) return;
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 10px 30px rgba(15,23,42,0.05)';
              }}
            >
              <Group justify="space-between" align="flex-start">
                <Stack gap={6}>
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase" style={{ letterSpacing: '0.5px' }}>
                    {stat.title}
                  </Text>
                  <Title order={3} fw={900} style={{ fontSize: rem(30), lineHeight: 1.1 }}>
                    {stat.value}
                  </Title>
                </Stack>
                <ThemeIcon size={54} radius="xl" variant="light" color={stat.color}>
                  <stat.icon style={{ width: rem(28), height: rem(28) }} stroke={1.6} />
                </ThemeIcon>
              </Group>

              <Group mt="lg" justify="space-between" align="center">
                <Text size="sm" c="dimmed" style={{ maxWidth: '82%', lineHeight: 1.6 }}>
                  {stat.desc}
                </Text>
                {stat.clickable && (
                  <ActionIcon variant="light" color={stat.color} radius="xl" size="lg">
                    <IconArrowRight size={18} />
                  </ActionIcon>
                )}
              </Group>
            </Paper>
          ))}
        </SimpleGrid>

        {/* LOKASYON / ŞANTİYE DAĞILIMI */}
        <Paper radius="xl" withBorder p="lg" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', boxShadow: '0 10px 30px rgba(15,23,42,0.05)' }}>
          <Stack gap="lg">
            <Group justify="space-between" align="center" wrap="wrap">
              <div>
                <Title order={4} fw={800}>Personel Dağılımı</Title>
                <Text size="sm" c="dimmed">Merkez ve şantiyelere göre mevcut çalışan yoğunluğu</Text>
              </div>
              <Badge size="lg" radius="xl" variant="light" color="blue">
                {stats.total_employees || 0} Toplam Personel
              </Badge>
            </Group>

            <Paper radius="xl" p="md" style={{ background: 'var(--mantine-color-gray-0)', border: '1px solid var(--mantine-color-gray-2)' }}>
              <Progress.Root size={24} radius="xl">
                {Object.entries(stats.location_counts || {}).map(([loc, count]) => {
                  if (count === 0 || stats.total_employees === 0) return null;
                  const config = locationConfig[loc] || { color: 'gray' };
                  const percent = (count / stats.total_employees) * 100;

                  return (
                    <Tooltip label={`${loc}: ${count} Kişi (%${percent.toFixed(0)})`} key={loc}>
                      <Progress.Section value={percent} color={config.color}>
                        <Progress.Label>{percent > 8 ? `${Math.round(percent)}%` : ''}</Progress.Label>
                      </Progress.Section>
                    </Tooltip>
                  );
                })}
              </Progress.Root>
            </Paper>

            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
              {Object.entries(stats.location_counts || {}).map(([loc, count]) => {
                const config = locationConfig[loc] || { icon: IconMapPin, color: 'gray', label: loc };

                return (
                  <Paper
                    key={loc}
                    withBorder
                    radius="xl"
                    p="md"
                    style={{ opacity: count > 0 ? 1 : 0.58, background: '#fff', borderColor: 'rgba(226,232,240,0.95)' }}
                  >
                    <Group justify="space-between" align="center" mb={8}>
                      <ThemeIcon variant="light" color={config.color} size={42} radius="xl">
                        <config.icon size={22} />
                      </ThemeIcon>
                      <Text fw={900} size="xl">{count}</Text>
                    </Group>
                    <Text size="sm" fw={700}>{config.label}</Text>
                    <Text size="xs" c="dimmed">Lokasyon mevcudu</Text>
                  </Paper>
                );
              })}
            </SimpleGrid>
          </Stack>
        </Paper>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Paper withBorder radius="xl" p="lg">
            <Group justify="space-between" mb="md"><div><Title order={4} fw={800}>Yaklaşan Olaylar</Title><Text size="sm" c="dimmed">Takip edilmesi gereken önemli tarihler</Text></div><ThemeIcon variant="light" color="orange" radius="xl"><IconCalendarTime size={19} /></ThemeIcon></Group>
            <Stack gap="sm">{upcomingEvents.map((event) => <Paper key={event.id} withBorder radius="lg" p="sm"><Group wrap="nowrap"><ThemeIcon size="sm" radius="xl" color={event.color} variant="light"><IconUserExclamation size={14} /></ThemeIcon><div><Text size="sm" fw={700}>{event.title}</Text><Text size="xs" c="dimmed">{event.meta}</Text></div></Group></Paper>)}</Stack>
          </Paper>
          <Paper withBorder radius="xl" p="lg">
            <Group justify="space-between" mb="md"><div><Title order={4} fw={800}>Son Hareketler</Title><Text size="sm" c="dimmed">İK modülündeki son işlemler</Text></div><ThemeIcon variant="light" color="blue" radius="xl"><IconFileCertificate size={19} /></ThemeIcon></Group>
            <Stack gap="sm">{recentActivities.map((activity) => <Paper key={activity.id} withBorder radius="lg" p="sm"><Text size="sm" fw={700}>{activity.title}</Text><Text size="xs" c="dimmed">{activity.meta}</Text></Paper>)}</Stack>
          </Paper>
        </SimpleGrid>
      </Stack>

      {/* DETAY LİSTESİ MODALI (İZİN / EVRAK) */}
      <Modal
        opened={listOpened}
        onClose={closeList}
        centered
        size="xl"
        padding="lg"
        radius="xl"
        title={
          <Group gap="sm">
            <ThemeIcon color={modalInfo.color} variant="light" radius="xl" size="lg">
              <modalInfo.icon size={20} />
            </ThemeIcon>
            <Text fw={800}>{modalInfo.title}</Text>
          </Group>
        }
      >
        <Box pos="relative" mih={220}>
          <LoadingOverlay visible={loadingList} zIndex={100} overlayProps={{ radius: 'sm', blur: 1 }} />
          <ScrollArea h={380} offsetScrollbars type="auto">
            <Paper withBorder radius="lg" style={{ overflow: 'hidden', background: '#fff' }}>
              <Table stickyHeader striped highlightOnHover verticalSpacing="md" horizontalSpacing="md">
                <Table.Thead style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)' }}>
                  <Table.Tr>
                    <Table.Th>Personel Bilgisi</Table.Th>
                    <Table.Th>{alertType === 'leave' ? 'İzin Tipi' : 'Evrak Tipi'}</Table.Th>
                    <Table.Th>{alertType === 'leave' ? 'Dönüş Tarihi' : 'Durum'}</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>İşlem</Table.Th>
                  </Table.Tr>
                </Table.Thead>

                <Table.Tbody>
                  {alertList.length > 0 ? (
                    alertList.map((item) => {
                      const status = getDaysLeft(item.target_date, item.isMissing);

                      return (
                        <Table.Tr key={item.id}>
                          <Table.Td>
                            <Stack gap={2}>
                              <Text fw={700} size="sm">{item.name}</Text>
                              <Text size="xs" c="dimmed">{item.role}</Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>{item.info}</Text>
                          </Table.Td>
                          <Table.Td>
                            {alertType === 'leave' ? (
                              <Text size="sm">{dayjs(item.target_date).format('DD MMMM YYYY')}</Text>
                            ) : (
                              <Badge color={status.color} variant={status.variant} radius="xl">
                                {status.text}
                              </Badge>
                            )}
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Tooltip label="Profili Görüntüle">
                              <ActionIcon
                                variant="light"
                                color="blue"
                                radius="xl"
                                size="lg"
                                onClick={() => handleEmployeeDetailClick(item)}
                              >
                                <IconSearch size={17} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  ) : (
                    !loadingList && (
                      <Table.Tr>
                        <Table.Td colSpan={4}>
                          <Center py="xl" style={{ flexDirection: 'column' }}>
                            <ThemeIcon color="gray" variant="light" size={68} radius="xl" mb="md">
                              <IconShieldCheck size={34} />
                            </ThemeIcon>
                            <Text c="dimmed" fw={600}>Kritik durumda kayıt bulunamadı.</Text>
                          </Center>
                        </Table.Td>
                      </Table.Tr>
                    )
                  )}
                </Table.Tbody>
              </Table>
            </Paper>
          </ScrollArea>
          <Group justify="flex-end" mt="lg">
            <Button variant="default" radius="xl" onClick={closeList}>Kapat</Button>
          </Group>
        </Box>
      </Modal>

      <EmployeeProfileModal
        opened={profileOpened}
        onClose={closeProfile}
        employee={selectedEmployee}
      />

      <style>
        {`
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      </Box>
  );
}
